import { i18n } from "i18next";
import React from "react";
import { Trans } from "react-i18next";

/**
 * Helper function to extract text content from React.ReactNode
 * Handles mixed content with text strings, numbers, and React elements
 * Recursively traverses through nested React elements to extract all text
 */
export const extractTextFromChildren = (
  children: React.ReactNode,
  i18n?: any
): string => {
  if (children === null || children === undefined) {
    return "";
  }

  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children
      .map((child) => extractTextFromChildren(child, i18n))
      .join("");
  }

  // Handle React elements - recursively extract text from their children
  if (typeof children === "object" && children !== null) {
    const reactElement = children as React.ReactElement;

    // Handle Trans component - get translation directly from i18n
    if (reactElement.type === Trans && reactElement.props?.i18nKey && i18n) {
      return i18n.t(reactElement.props.i18nKey);
    }

    // Check if element has children prop and recursively extract
    if (reactElement.props?.children !== undefined) {
      return extractTextFromChildren(reactElement.props.children, i18n);
    }

    // Check for other text-related props (e.g., value, content, etc.)
    // Some components might use different prop names for text content
    const textProps = ["value", "content", "text", "defaultValue"];
    for (const prop of textProps) {
      if (reactElement.props?.[prop] !== undefined) {
        const textValue = extractTextFromChildren(
          reactElement.props[prop],
          i18n
        );
        if (textValue) return textValue;
      }
    }
  }

  return "";
};

/**
 * Helper function to detect if React.ReactNode contains Korean characters
 * Korean characters (Hangul) are in the Unicode ranges:
 * - Hangul Jamo: U+1100–U+11FF
 * - Hangul Compatibility Jamo: U+3130–U+318F
 * - Hangul Syllables: U+AC00–U+D7AF
 * - Hangul Jamo Extended-A: U+A960–U+A97F
 * - Hangul Jamo Extended-B: U+D7B0–U+D7FF
 */

export const containsKoreanCharacters = (
  children: React.ReactNode,
  i18n: i18n
): boolean => {
  const text = extractTextFromChildren(children, i18n);

  if (!text) return false;

  // Regular expression to match Korean characters
  // Returns true if ANY Korean character is found, even when mixed with English/other text
  const koreanRegex =
    /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\uA960-\uA97F\uD7B0-\uD7FF]/;
  return koreanRegex.test(text);
};

export const handlingDynamicFontBasedOnLanguage = (
  currentLanguage: string,
  children: React.ReactNode,
  i18n: i18n
): string => {
  const hasKoreanChars = containsKoreanCharacters(children, i18n);

  if (!hasKoreanChars && currentLanguage === "ko") return "non-kr-character";

  if (hasKoreanChars && currentLanguage !== "ko") return "kr-character";

  // add another condition here for future implem and handle the loading of Head and Body font in the Typography.scss
  return "";
};
